import React from 'react';
import { Box, Container } from '@mui/material';
import Header from './Header';
import QuotaUsageBanner from '../common/QuotaUsageBanner';
import TrialStatusBanner from '../TrialStatusBanner';
import { useAuth } from '../../contexts/AuthContext';
import { useTrialStatus } from '../../hooks/useTrialStatus';
import { ThemeModeProvider } from '../../contexts/ThemeModeContext';
import FeedbackButton from '../feedback/FeedbackButton';
import FeedbackModal from '../feedback/FeedbackModal';

interface InternalPageLayoutProps {
  children: React.ReactNode;
  maxWidth?: 'xs' | 'sm' | 'md' | 'lg' | 'xl' | false;
  disablePadding?: boolean;
}

const InternalPageLayout: React.FC<InternalPageLayoutProps> = ({ 
  children, 
  maxWidth = 'xl',
  disablePadding = false 
}) => {
  const { user } = useAuth();
  const { isTrialExpired, isTrialExpiringSoon, daysUntilExpiry } = useTrialStatus();
  const isAdmin = user?.role === 'ADMIN';
  
  return (
    <ThemeModeProvider>
      <Box
      sx={{
        minHeight: '100vh',
        width: '100%',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        position: 'relative',
        // Use backgroundImage so backgroundColor (bgcolor) remains intact
        backgroundImage: (theme) => theme.palette.mode === 'dark'
          ? `
            radial-gradient(circle at 20% 80%, rgba(255,255,255,0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(255,255,255,0.02) 0%, transparent 50%)
          `
          : `
            linear-gradient(135deg, rgba(0, 122, 158, 0.02) 0%, rgba(0, 51, 78, 0.05) 100%),
            radial-gradient(circle at 20% 80%, rgba(0, 122, 158, 0.03) 0%, transparent 50%),
            radial-gradient(circle at 80% 20%, rgba(0, 51, 78, 0.03) 0%, transparent 50%)
          `,
        backgroundRepeat: 'no-repeat',
        backgroundSize: 'cover',
      }}
      >
        <Header />
        <QuotaUsageBanner />
        {isTrialExpired && <TrialStatusBanner status="expired" isAdmin={isAdmin} />}
        {!isTrialExpired && isTrialExpiringSoon && (
          <TrialStatusBanner 
            status="expiring" 
            daysRemaining={daysUntilExpiry || undefined} 
            isAdmin={isAdmin} 
          />
        )}
        
        <Box 
          component="main" 
          sx={{ 
            flexGrow: 1, 
            position: 'relative', 
            zIndex: 2,
            ...(disablePadding ? {} : { py: { xs: 3, md: 4 } })
          }}
        >
          {maxWidth === false ? (
            <Box sx={{ width: '100%', ...(disablePadding ? {} : { px: { xs: 2, sm: 3, md: 4 } }) }}>
              {children}
            </Box>
          ) : (
            <Container 
              maxWidth={maxWidth} 
              sx={{ 
                ...(disablePadding ? { p: 0 } : {})
              }}
            >
              {children}
            </Container>
          )}
        </Box>
      </Box>
      
      {/* Feedback components - only available in internal pages */}
      <FeedbackButton />
      <FeedbackModal />
    </ThemeModeProvider>
  );
};

export default InternalPageLayout; 
